import React from 'react';
import { Description, Field } from '@headlessui/react';
import Button from 'flow/components/ui-components/Button';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import { ConsentCategories } from './TrackingConsentModal';

interface ToggleRowProps {
    title: string;
    description: string;
    value: boolean;
    onChange: (val: boolean) => void;
}

// The switch is the only control. It takes the keyboard, and it alone toggles
// the category. The row is a HeadlessUI Field, as in SettingRow, so the
// description below becomes the accessible description of the switch.
const ToggleRow = ({ title, description, value, onChange }: ToggleRowProps) => (
    <Field
        as="div"
        className="flex w-full flex-col gap-y-2 rounded-sm px-2 py-4 hover:bg-gray-100"
    >
        <div className="flex w-full justify-between">
            <span className="flex items-center text-base text-gray-800">
                {title}
            </span>
            <div className="flex shrink-0 items-center">
                <Toggle value={value} onChange={onChange} text={title} />
            </div>
        </div>
        <Description as="span" className="text-sm text-gray-500">
            {description}
        </Description>
    </Field>
);

interface Props {
    categories: ConsentCategories;
    onCategoryChange: (key: keyof ConsentCategories, value: boolean) => void;
    onAcceptSelected: () => void;
}

const TrackingConsentCustomise = ({
    categories,
    onCategoryChange,
    onAcceptSelected,
}: Props) => {
    const { i18next } = useAppContext();

    return (
        <div className="flex h-full flex-col px-6 pb-6">
            <p className="mb-2 text-sm text-gray-500">
                {i18next.t('settings.general.tracking.customise.description')}
            </p>

            <ToggleRow
                title={i18next.t('cookie.category.analytics.title')}
                description={i18next.t('cookie.category.analytics.description')}
                value={categories.analytics}
                onChange={(val) => onCategoryChange('analytics', val)}
            />
            <ToggleRow
                title={i18next.t('cookie.category.errorMonitoring.title')}
                description={i18next.t(
                    'cookie.category.errorMonitoring.description',
                )}
                value={categories.errorMonitoring}
                onChange={(val) => onCategoryChange('errorMonitoring', val)}
            />

            <div className="flex w-full flex-col gap-y-2 rounded-sm px-2 py-4">
                <div className="flex w-full justify-between">
                    <span className="flex items-center text-base text-gray-800">
                        {i18next.t('settings.general.tracking.necessary.title')}
                    </span>
                    <span className="flex shrink-0 items-center text-sm whitespace-nowrap text-gray-500">
                        {i18next.t('settings.general.tracking.necessary.label')}
                    </span>
                </div>
                <span className="text-sm text-gray-500">
                    {i18next.t(
                        'settings.general.tracking.necessary.description',
                    )}
                </span>
            </div>

            <div className="mt-auto">
                <Button
                    color="ghost"
                    className="w-full py-3"
                    onClick={onAcceptSelected}
                >
                    {i18next.t('settings.general.tracking.accept.selected')}
                </Button>
            </div>
        </div>
    );
};

export default TrackingConsentCustomise;
